import React from 'react';
import { Tag } from 'antd';
import type { TFunction } from 'i18next';

// snapshot-pair history：
// 後端 edit_*_items history 的 description 同時帶 before_items / after_items 完整品項列表。
// 此 component 上下並列兩個獨立 spreadsheet-style 表格：欄位橫向展開（columns = field names）、
// 每個品項一橫列（rows = items）。reviewer 自行判斷差異，不做跨 section sku 配對。

export interface ItemSnapshot {
  sku?: string | null;
  name?: string | null;
  quantity?: number | null;
  custom_attributes?: Record<string, unknown> | null;
  [key: string]: unknown;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// 顯示順序排在前面的欄位。sku 為第一欄（row header），其餘 PREFERRED 接續，其餘字母序殿後。
const PREFERRED_KEYS = [
  'sku',
  'name',
  'quantity',
  'scheduled_quantity',
  'price',
  'original_price',
  'cost',
  'unit_price',
  'batch',
  'expiration_date',
  'manufacturing_date',
  'inventory_type_name',
  'role',
  'note',
  'internal_note',
  'identifier',
  'custom_attributes'
] as const;

// 從所有 item 收集欄位 union，再用 PREFERRED_KEYS 排序、剩餘字母序殿後。
function collectColumns(items: ItemSnapshot[]): string[] {
  const present = new Set<string>();
  items.forEach((item) => {
    Object.keys(item).forEach((k) => present.add(k));
  });

  const preferred = PREFERRED_KEYS.filter((k) => present.has(k));
  const preferredSet = new Set<string>(PREFERRED_KEYS);
  const others = Array.from(present)
    .filter((k) => !preferredSet.has(k))
    .sort();
  return [...preferred, ...others];
}

function fieldLabel(t: TFunction, key: string, fieldKeyPrefix?: string): string {
  if (!fieldKeyPrefix) return key;
  return t(`${fieldKeyPrefix}.${key}`, { defaultValue: key }) as string;
}

// 外框依 section 著色（紅=before、綠=after），內部 cell 邊框用中性灰降噪。
// 外框厚度對齊內部 cell 的 1px（既有專案慣例），加 borderRadius 4px 圓角；
// 邊框與 borderRadius 都套在 wrapper div 上（border-collapse:collapse 不認 border-radius）。
const BEFORE_BORDER_COLOR = '#ff7875';
const AFTER_BORDER_COLOR = '#95de64';
const TABLE_BORDER_RADIUS = 4;

function createWrapperStyle(borderColor: string): React.CSSProperties {
  return {
    border: `1px solid ${borderColor}`,
    borderRadius: TABLE_BORDER_RADIUS,
    overflow: 'auto'
  };
}

const TABLE_STYLE: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  backgroundColor: 'transparent',
  fontSize: 12
};

const HEADER_CELL_STYLE: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid #f0f0f0',
  fontWeight: 600,
  fontSize: 12,
  textAlign: 'left',
  color: '#555',
  whiteSpace: 'nowrap',
  verticalAlign: 'top'
};

const BODY_CELL_STYLE: React.CSSProperties = {
  padding: '4px 8px',
  border: '1px solid #f0f0f0',
  verticalAlign: 'top',
  wordBreak: 'break-word'
};

// custom_attributes 在 cell 內用 Tag 列表呈現，每個 attr 一顆 Tag。
function renderCustomAttributesTags(value: unknown): React.ReactNode {
  if (value === null || value === undefined) return '—';
  if (typeof value !== 'object' || Array.isArray(value)) return formatValue(value);

  const attrs = value as Record<string, unknown>;
  const sortedKeys = Object.keys(attrs).sort();
  if (sortedKeys.length === 0) return '—';

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {sortedKeys.map((k) => (
        <Tag key={k} style={{ marginRight: 0 }}>
          {k}: {formatValue(attrs[k])}
        </Tag>
      ))}
    </div>
  );
}

function renderCell(key: string, value: unknown): React.ReactNode {
  if (key === 'custom_attributes') return renderCustomAttributesTags(value);
  return formatValue(value);
}

function bySkuAsc(a: ItemSnapshot, b: ItemSnapshot): number {
  const skuA = a.sku ?? '';
  const skuB = b.sku ?? '';
  return skuA.localeCompare(skuB);
}

interface SectionTableProps {
  title: string;
  items: ItemSnapshot[];
  columns: string[];
  fieldKeyPrefix?: string;
  t: TFunction;
  borderColor: string;
}

function SectionTable({ title, items, columns, fieldKeyPrefix, t, borderColor }: SectionTableProps) {
  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 4, color: borderColor }}>{title}</div>
      {items.length === 0 ? (
        <span style={{ color: '#999' }}>—</span>
      ) : (
        <div style={createWrapperStyle(borderColor)}>
          <table style={TABLE_STYLE}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col} style={HEADER_CELL_STYLE} scope="col">
                    {fieldLabel(t, col, fieldKeyPrefix)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={`${item.sku ?? 'NO_SKU'}-${idx}`}>
                  {columns.map((col) => (
                    <td key={col} style={BODY_CELL_STYLE}>
                      {renderCell(col, item[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface ItemsSnapshotViewProps {
  beforeItems: ItemSnapshot[];
  afterItems: ItemSnapshot[];
  t: TFunction;
  fieldKeyPrefix?: string;
}

export function ItemsSnapshotView({ beforeItems, afterItems, t, fieldKeyPrefix }: ItemsSnapshotViewProps) {
  const safeBefore = Array.isArray(beforeItems) ? beforeItems : [];
  const safeAfter = Array.isArray(afterItems) ? afterItems : [];

  if (safeBefore.length === 0 && safeAfter.length === 0) {
    return <span style={{ color: '#999' }}>—</span>;
  }

  // 兩個 section 共用同一組 columns，避免欄位順序不一致造成 reviewer 視覺對齊困難。
  const columns = collectColumns([...safeBefore, ...safeAfter]);

  const sortedBefore = [...safeBefore].sort(bySkuAsc);
  const sortedAfter = [...safeAfter].sort(bySkuAsc);

  const beforeTitle = t('shared.history.items_snapshot.before', { defaultValue: 'Before' }) as string;
  const afterTitle = t('shared.history.items_snapshot.after', { defaultValue: 'After' }) as string;

  return (
    <div style={{ marginTop: 4, paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionTable
        title={beforeTitle}
        items={sortedBefore}
        columns={columns}
        fieldKeyPrefix={fieldKeyPrefix}
        t={t}
        borderColor={BEFORE_BORDER_COLOR}
      />
      <SectionTable
        title={afterTitle}
        items={sortedAfter}
        columns={columns}
        fieldKeyPrefix={fieldKeyPrefix}
        t={t}
        borderColor={AFTER_BORDER_COLOR}
      />
    </div>
  );
}

export default ItemsSnapshotView;
